import {
  ActionCommand,
  DateString,
  ShiftUpdateParams,
  TimeString,
  User,
  buildEditShiftAction,
} from '@nextforce/api'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { errorAlert } from 'shared/alerts'
import { useAddShift } from './queries/add'
import { usePerformShiftAction } from './queries/perform_action'

export interface ShiftActionsContextValue {
  loading: boolean
  shiftParams: ShiftUpdateParams
  selectedUser?: User
  actionCommand?: ActionCommand
  setSelectedUser: React.Dispatch<React.SetStateAction<User | undefined>>
  setShiftParams: React.Dispatch<React.SetStateAction<ShiftUpdateParams>>
  setActionCommand: React.Dispatch<
    React.SetStateAction<ActionCommand | undefined>
  >
  onCreateShift(splitShift?: boolean, user?: User): Promise<void>
  onPerformSwapCover(user: User): Promise<void>
  onEditShift(): Promise<void>
}

export const ShiftActionsContext = createContext({} as ShiftActionsContextValue)

const defaultState: ShiftUpdateParams = {
  date: '' as DateString,
  startTime: '' as TimeString,
  endTime: '' as TimeString,
}

interface Props {
  children: React.ReactNode
}

export function ShiftActionsProvider(props: Props) {
  const { handleNavigation } = useRoutingContext()

  const [shiftParams, setShiftParams] =
    useState<ShiftUpdateParams>(defaultState)
  const [selectedUser, setSelectedUser] = useState<User>()
  const [actionCommand, setActionCommand] = useState<ActionCommand>()
  const [loading, setLoading] = useState(false)

  const addShiftMutation = useAddShift()
  const shiftMutation = usePerformShiftAction()

  const onCreateShift = useCallback(
    async (splitShift?: boolean, user?: User) => {
      setLoading(true)
      try {
        const shiftData = await addShiftMutation.mutateAsync({
          ...shiftParams,
          splitShift,
          userId: user?.id,
        })

        if (shiftData) {
          setShiftParams(defaultState)
          setSelectedUser(undefined)
          handleNavigation({
            routeName: 'confirm_shift_action',
            routeParams: { shiftData },
          })
        }
      } catch (err: any) {
        errorAlert(err, t('createShiftErrorTitle'))
      }
      setLoading(false)
    },
    [addShiftMutation, handleNavigation, shiftParams]
  )

  const onEditShift = useCallback(async () => {
    if (actionCommand) {
      setLoading(true)
      const action = buildEditShiftAction(actionCommand.action, shiftParams)
      try {
        await shiftMutation.mutateAsync(action)
        setLoading(false)
        setActionCommand(undefined)
        handleNavigation({
          routeName: 'schedule',
          routeParams: { date: shiftParams.date },
        })
      } catch (err: any) {
        setLoading(false)
        errorAlert(err, t('editShiftErrorTitle'))
      }
    }
  }, [actionCommand, handleNavigation, shiftMutation, shiftParams])

  const onPerformSwapCover = useCallback(
    async (user: User) => {
      if (!actionCommand?.action) {
        return
      }
      try {
        const res = await shiftMutation.mutateAsync({
          ...actionCommand.action,
          params: { ...actionCommand.action.params, user_id: user.id },
        })
        if (res) {
          setActionCommand(undefined)
          handleNavigation({
            routeName: 'swap_cover_shift',
            routeParams: { shiftData: res },
          })
        }
      } catch (err: any) {
        errorAlert(err, t('assignShiftErrorTitle'))
      }
    },
    [actionCommand, handleNavigation, shiftMutation]
  )

  useEffect(() => {
    if (selectedUser) {
      onCreateShift(false, selectedUser).catch(e => null)
      setSelectedUser(undefined)
    }
  }, [onCreateShift, selectedUser])

  return (
    <ShiftActionsContext.Provider
      value={{
        loading,
        shiftParams,
        selectedUser,
        actionCommand,
        setSelectedUser,
        setShiftParams,
        setActionCommand,
        onCreateShift,
        onPerformSwapCover,
        onEditShift,
      }}
    >
      {props.children}
    </ShiftActionsContext.Provider>
  )
}

export function useShiftActions() {
  return useContext<ShiftActionsContextValue>(ShiftActionsContext)
}
